import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type RatingsResponse } from "../api";
import { useAuth } from "../auth";
import { Avatar } from "./Avatar";
import { Card, SectionHeading, Spinner } from "./ui";
import { StarRating } from "./StarRating";

interface Props {
  bookId: number;
}

export function BookRatings({ bookId }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const list = useQuery({
    queryKey: ["ratings", bookId],
    queryFn: () => api.get<RatingsResponse>(`/ratings/${bookId}`),
  });

  const set = useMutation({
    mutationFn: (payload: { stars: number; review: string | null }) =>
      api.post(`/ratings/${bookId}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ratings", bookId] }),
  });

  const ratings = list.data?.ratings ?? [];
  const mine = ratings.find((r) => r.userId === user?.id) ?? null;
  const others = ratings.filter((r) => r.userId !== user?.id);

  const [stars, setStars] = useState(mine?.stars ?? 0);
  const [review, setReview] = useState(mine?.review ?? "");

  useEffect(() => {
    if (mine) {
      setStars(mine.stars);
      setReview(mine.review ?? "");
    }
  }, [mine?.userId, mine?.stars, mine?.review]);

  if (list.isLoading) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }

  return (
    <Card accent>
      <SectionHeading
        eyebrow="What the club thought"
        title="Ratings & reviews"
        description={
          list.data?.average
            ? `Average ${list.data.average.toFixed(1)} stars across ${list.data.count} ${list.data.count === 1 ? "rating" : "ratings"}.`
            : "No ratings yet."
        }
      />

      <div className="mt-6 p-5 rounded-xl bg-cream-50/70 border border-cream-200 flex flex-col gap-3">
        <div className="text-[0.7rem] uppercase tracking-[0.18em] text-gold-dark">
          Your rating
        </div>
        <div className="flex items-center gap-3">
          <StarRating value={stars} onChange={setStars} size={26} />
        </div>
        <textarea
          rows={3}
          value={review}
          onChange={(e) => setReview(e.target.value)}
          placeholder="A line or two about how it landed for you…"
          className="w-full px-3 py-2.5 rounded-lg bg-white border border-cream-300 text-sm text-plum placeholder:text-plum-mute placeholder:italic resize-none focus:outline-none focus:border-rose"
        />
        <div className="flex justify-end">
          <button
            disabled={stars === 0 || set.isPending}
            onClick={() => set.mutate({ stars, review: review.trim() || null })}
            className="text-xs uppercase tracking-[0.14em] px-4 py-1.5 rounded-full bg-rose text-white hover:bg-rose-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {set.isPending ? "Saving…" : mine ? "Update review" : "Save rating"}
          </button>
        </div>
      </div>

      {others.length > 0 ? (
        <div className="mt-7 flex flex-col gap-5">
          {others.map((r) => (
            <div key={r.userId} className="flex gap-4">
              <Avatar name={r.name} email={r.email} size="md" />
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-medium text-plum">
                    {r.name ?? r.email.split("@")[0]}
                  </span>
                  <StarRating value={r.stars} readOnly size={16} />
                </div>
                {r.review ? (
                  <p className="text-plum-soft text-sm leading-relaxed italic mt-2 font-serif">
                    "{r.review}"
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}
